import React, { useMemo, useState } from "react";
import { Check } from "lucide-react";
import {
  STUDIO_ACTIONS,
  displayRoleName,
  enabledPermissionCount,
  initials,
  moduleFlags,
  moduleTitle,
  orderedModuleKeys,
  roleTone,
} from "./iamStudio";

function roleBlurb(role) {
  const description = String(role.description || "").trim();
  const name = displayRoleName(role.name);
  if (description && description.toLowerCase() !== name.toLowerCase() && description.toLowerCase() !== String(role.name || "").trim().toLowerCase()) {
    return description;
  }
  return "What this role can open. Change the ticks on Roles.";
}

export default function IamPermissionTemplatesStudio({ roles, catalog, loading, onOpenRoles }) {
  const [roleId, setRoleId] = useState(null);
  const modules = orderedModuleKeys(catalog);
  const totalSlots = modules.length * STUDIO_ACTIONS.length;
  const active = useMemo(() => {
    if (!roles.length) return null;
    return roles.find((r) => r.role_id === roleId) || roles[0];
  }, [roles, roleId]);

  const rows = useMemo(() => {
    if (!active || !catalog) return [];
    return modules.map((moduleKey) => {
      const flags = moduleFlags(active.permission_matrix, moduleKey, catalog.modules[moduleKey]);
      const on = STUDIO_ACTIONS.filter((action) => flags[action.key]).length;
      return { moduleKey, flags, on };
    });
  }, [active, catalog, modules]);

  const enabled = active && catalog ? enabledPermissionCount(active.permission_matrix, catalog) : 0;
  const percent = totalSlots ? Math.round((enabled / totalSlots) * 100) : 0;
  const openModules = rows.filter((row) => row.on > 0);

  return (
    <div className="iam-tpl">
      <header className="iam-ux-head">
        <div>
          <h2>Permission Templates</h2>
          <p>Pick a role to see exactly what it can view, add, edit, delete, approve, and export.</p>
        </div>
        <span className="iam-ux-count">{roles.length} {roles.length === 1 ? "template" : "templates"}</span>
      </header>

      {loading || !catalog ? <p className="iam-ux-empty">Loading permission templates…</p> : !roles.length ? (
        <div className="iam-ux-card">
          <div className="iam-ux-zero">
            <strong>No templates yet</strong>
            <p>Templates appear as soon as you create a role. Start with Admin, HR, Manager, or Employee.</p>
            {onOpenRoles && (
              <button type="button" className="iam-ux-add" onClick={onOpenRoles}>Create a role</button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="iam-tpl-roles" role="tablist" aria-label="Roles">
            {roles.map((role) => {
              const tone = roleTone(role.name);
              const on = active?.role_id === role.role_id;
              const count = enabledPermissionCount(role.permission_matrix, catalog);
              return (
                <button
                  key={role.role_id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  className={`iam-tpl-role ${tone} ${on ? "on" : ""}`}
                  onClick={() => setRoleId(role.role_id)}
                >
                  <span className={`iam-studio-avatar ${tone}`}>{initials(displayRoleName(role.name))}</span>
                  <span>
                    <strong>{displayRoleName(role.name)}</strong>
                    <small>{count} permissions · {role.assigned_user_count || 0} users</small>
                  </span>
                </button>
              );
            })}
          </div>

          {active && (
            <div className="iam-tpl-layout">
              <section className="iam-tpl-board">
                <div className="iam-tpl-board-head">
                  <div>
                    <h3>{displayRoleName(active.name)}</h3>
                    <p>{roleBlurb(active)}</p>
                  </div>
                  <div className="iam-tpl-meter">
                    <div>
                      <strong>{enabled}</strong>
                      <span>of {totalSlots}</span>
                    </div>
                    <div className="iam-tpl-bar" aria-hidden="true">
                      <i style={{ width: `${percent}%` }} />
                    </div>
                    <small>{percent}% of this template is on</small>
                  </div>
                  <span className={`iam-ux-status ${active.status === "inactive" ? "off" : "on"}`}>
                    <i />
                    {active.status === "inactive" ? "Inactive" : "Active"}
                  </span>
                </div>
                <div className="iam-tpl-table-wrap">
                  <table className="iam-tpl-table">
                    <thead>
                      <tr>
                        <th>Module</th>
                        {STUDIO_ACTIONS.map((action) => <th key={action.key}>{action.label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.moduleKey} className={row.on === STUDIO_ACTIONS.length ? "full" : row.on ? "some" : "none"}>
                          <td>
                            <strong>{moduleTitle(row.moduleKey)}</strong>
                            <small>{row.on} of {STUDIO_ACTIONS.length}</small>
                          </td>
                          {STUDIO_ACTIONS.map((action) => {
                            const granted = !!row.flags[action.key];
                            return (
                              <td key={action.key}>
                                <span
                                  className={`iam-tpl-cell ${granted ? "on" : "off"}`}
                                  title={`${moduleTitle(row.moduleKey)} · ${action.label} · ${granted ? "granted" : "not granted"}`}
                                >
                                  {granted ? <Check size={14} strokeWidth={3} /> : null}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <aside className="iam-tpl-side">
                <h3>This role</h3>
                <dl>
                  <div><dt>Name</dt><dd>{displayRoleName(active.name)}</dd></div>
                  <div><dt>People using it</dt><dd>{active.assigned_user_count || 0}</dd></div>
                  <div><dt>Modules open</dt><dd>{openModules.length} of {modules.length}</dd></div>
                </dl>
                <h3>Can open</h3>
                {openModules.length ? (
                  <ul>
                    {openModules.map((row) => (
                      <li key={row.moduleKey}>
                        <span>{moduleTitle(row.moduleKey)}</span>
                        <em>{row.on}/{STUDIO_ACTIONS.length}</em>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No module is switched on yet.</p>
                )}
                <div className="iam-tpl-legend">
                  <span className="iam-tpl-cell on"><Check size={12} strokeWidth={3} /></span>
                  Granted
                  <span className="iam-tpl-cell off" />
                  Not granted
                </div>
                {onOpenRoles && (
                  <button type="button" className="iam-ux-add" onClick={onOpenRoles}>Edit on Roles</button>
                )}
              </aside>
            </div>
          )}
        </>
      )}
    </div>
  );
}
